import { z } from 'zod';
import { WorkItemSchema, WorkItemStatusSchema, WorkItemTypeSchema, WorkItemPrioritySchema } from './pm-contracts.js';
export const ListWorkItemsInputSchema = z.object({
    assignees: z.array(z.string()).optional().describe('Filter by assignee usernames'),
    statuses: z.array(WorkItemStatusSchema).optional().describe('Filter by status'),
    types: z.array(WorkItemTypeSchema).optional().describe('Filter by type'),
    priorities: z.array(WorkItemPrioritySchema).optional().describe('Filter by priority'),
    title_contains: z.string().optional().describe('Search in work item titles'),
    description_contains: z.string().optional().describe('Search in work item descriptions'),
    labels: z.array(z.string()).optional().describe('Filter by GitHub labels'),
    unassigned: z.boolean().optional().describe('Show only unassigned work items'),
    agent_id: z.string().optional().describe('Filter to work items assigned to this agent'),
    created_after: z.string().optional().describe('Show items created after this date (ISO format)'),
    updated_after: z.string().optional().describe('Show items updated after this date (ISO format)'),
    limit: z.number().positive().max(100).default(20).describe('Maximum number of items to return (max 100)'),
    offset: z.number().nonnegative().default(0).describe('Number of items to skip for pagination'),
    sort_by: z.enum(['created_at', 'updated_at', 'priority', 'title']).default('updated_at').describe('Field to sort by'),
    sort_order: z.enum(['asc', 'desc']).default('desc').describe('Sort order')
}).describe('Parameters for searching and filtering work items');
export const ListWorkItemsOutputSchema = z.object({
    success: z.boolean(),
    data: z.object({
        items: z.array(WorkItemSchema),
        total: z.number(),
        limit: z.number(),
        offset: z.number()
    }).optional(),
    error: z.string().optional()
});
export const GetWorkItemInputSchema = z.object({
    work_item_id: z.string().min(1).describe('Work item ID in format "owner/repo#number" (e.g., "qwickapps/qwickforge#123")')
}).describe('Parameters for retrieving a specific work item');
export const GetWorkItemOutputSchema = z.object({
    success: z.boolean(),
    data: WorkItemSchema.optional(),
    error: z.string().optional()
});
export const ClaimWorkItemOutputSchema = z.object({
    success: z.boolean(),
    data: z.object({
        work_item_id: z.string(),
        agent_id: z.string(),
        claimed_at: z.string()
    }).optional(),
    error: z.string().optional()
});
export const AddProgressUpdateOutputSchema = z.object({
    success: z.boolean(),
    data: z.object({
        update_id: z.string(),
        work_item_id: z.string(),
        timestamp: z.string()
    }).optional(),
    error: z.string().optional()
});
export const CreateWorkItemOutputSchema = z.object({
    success: z.boolean(),
    data: WorkItemSchema.optional(),
    error: z.string().optional()
});
export const AddCommentOutputSchema = z.object({
    success: z.boolean(),
    data: z.object({
        comment_id: z.string(),
        work_item_id: z.string(),
        created_at: z.string()
    }).optional(),
    error: z.string().optional()
});
export const FindAvailableWorkInputSchema = z.object({
    priority_filter: z.enum(['high_only', 'medium_and_above', 'all']).default('all').describe('Priority filter for available work'),
    work_types: z.array(WorkItemTypeSchema).default(['task', 'bug', 'feature']).describe('Types of work to include'),
    exclude_large_estimates: z.boolean().default(false).describe('Exclude items estimated over 40 hours'),
    limit: z.number().positive().max(50).default(10).describe('Maximum number of items to return')
}).describe('Find unassigned work items ready to be claimed and started');
export const FindAvailableWorkOutputSchema = z.object({
    success: z.boolean(),
    data: z.object({
        available_work: z.array(WorkItemSchema),
        count: z.number(),
        filters_applied: z.object({
            priority_filter: z.string(),
            work_types: z.array(z.string()),
            excluded_large: z.boolean()
        })
    }).optional(),
    error: z.string().optional()
});
export const FindMyAssignedWorkInputSchema = z.object({
    agent_id: z.string().min(1).describe('Agent ID to find assigned work for'),
    include_pending_review: z.boolean().default(true).describe('Include items waiting for review'),
    include_blocked: z.boolean().default(true).describe('Include blocked items'),
    sort_by: z.enum(['status_priority', 'due_date', 'priority']).default('status_priority').describe('How to sort the results')
}).describe('Find all work items currently assigned to a specific agent');
export const FindMyAssignedWorkOutputSchema = z.object({
    success: z.boolean(),
    data: z.object({
        assigned_work: z.array(WorkItemSchema),
        count: z.number(),
        summary: z.object({
            in_progress: z.number(),
            in_review: z.number(),
            blocked: z.number()
        })
    }).optional(),
    error: z.string().optional()
});
export const FindWorkReadyForReviewInputSchema = z.object({
    review_type: z.enum(['code_review', 'testing', 'design_review', 'all']).default('all').describe('Type of review needed'),
    priority_filter: z.enum(['high_only', 'medium_and_above', 'all']).default('all').describe('Priority filter for review items'),
    waiting_days_threshold: z.number().nonnegative().default(0).describe('Minimum days waiting for review'),
    limit: z.number().positive().max(50).default(10).describe('Maximum number of items to return')
}).describe('Find work items that need validation, testing, or review');
export const FindWorkReadyForReviewOutputSchema = z.object({
    success: z.boolean(),
    data: z.object({
        review_items: z.array(WorkItemSchema),
        count: z.number()
    }).optional(),
    error: z.string().optional()
});
export const FindBlockedWorkInputSchema = z.object({
    blocker_severity: z.enum(['critical', 'high', 'medium', 'all']).default('all').describe('Minimum blocker severity to include'),
    include_dependency_blocks: z.boolean().default(true).describe('Include items blocked by dependencies'),
    blocked_for_days: z.number().nonnegative().default(0).describe('Minimum days blocked'),
    limit: z.number().positive().max(50).default(20).describe('Maximum number of items to return')
}).describe('Find work items that are stuck due to dependencies or blockers');
export const FindBlockedWorkOutputSchema = z.object({
    success: z.boolean(),
    data: z.object({
        blocked_items: z.array(WorkItemSchema),
        count: z.number()
    }).optional(),
    error: z.string().optional()
});
export const FindUnplannedWorkInputSchema = z.object({
    work_types: z.array(WorkItemTypeSchema).default(['feature', 'epic', 'task']).describe('Types of work that need planning'),
    priority_filter: z.enum(['high_only', 'medium_and_above', 'all']).default('medium_and_above').describe('Priority filter for unplanned work'),
    min_estimation_confidence: z.number().min(0).max(1).default(0.7).describe('Exclude well-estimated items above this confidence'),
    limit: z.number().positive().max(50).default(15).describe('Maximum number of items to return')
}).describe('Find work items that lack implementation plans or detailed requirements');
export const FindUnplannedWorkOutputSchema = z.object({
    success: z.boolean(),
    data: z.object({
        unplanned_items: z.array(WorkItemSchema),
        count: z.number()
    }).optional(),
    error: z.string().optional()
});
export const FindRecentlyCompletedWorkInputSchema = z.object({
    days_back: z.number().positive().default(7).describe('How many days back to look for completed work'),
    work_types: z.array(WorkItemTypeSchema).default(['task', 'bug', 'feature']).describe('Types of completed work to include'),
    include_artifacts: z.boolean().default(true).describe('Include work items with artifacts in the response'),
    completed_by_agent: z.string().optional().describe('Filter to work completed by specific agent'),
    limit: z.number().positive().max(50).default(20).describe('Maximum number of items to return')
}).describe('Find work items recently completed for context and learning');
export const FindRecentlyCompletedWorkOutputSchema = z.object({
    success: z.boolean(),
    data: z.object({
        completed_items: z.array(WorkItemSchema),
        count: z.number()
    }).optional(),
    error: z.string().optional()
});
export const FindHighPriorityWorkInputSchema = z.object({
    priority_levels: z.array(WorkItemPrioritySchema).default(['critical', 'high']).describe('Priority levels to include'),
    include_statuses: z.array(WorkItemStatusSchema).default(['open', 'in_progress', 'review', 'blocked']).describe('Work item statuses to include'),
    urgent_threshold_days: z.number().nonnegative().default(3).describe('Items stale for N days become urgent'),
    limit: z.number().positive().max(50).default(15).describe('Maximum number of items to return')
}).describe('Find urgent or high-priority work items regardless of status');
export const FindHighPriorityWorkOutputSchema = z.object({
    success: z.boolean(),
    data: z.object({
        high_priority_items: z.array(WorkItemSchema),
        count: z.number()
    }).optional(),
    error: z.string().optional()
});
const SessionSchema = z.object({
    id: z.string(),
    title: z.string(),
    workspaceId: z.string(),
    createdAt: z.date(),
    updatedAt: z.date(),
    stats: z.object({
        prompt_count: z.number(),
        execution_count: z.number(),
        last_activity: z.date()
    }).optional()
});
export const SelectSessionsInputSchema = z.object({
    offset: z.number().nonnegative().default(0).describe('Number of sessions to skip for pagination'),
    limit: z.number().positive().max(100).default(100).describe('Maximum number of sessions to return (max 100)'),
    search: z.string().optional().describe('Search term to filter sessions by title'),
    created_after: z.string().optional().describe('Show sessions created after this date (ISO format)'),
    created_before: z.string().optional().describe('Show sessions created before this date (ISO format)'),
    sort_by: z.enum(['created_at', 'updated_at', 'title']).default('updated_at').describe('Field to sort by'),
    sort_order: z.enum(['asc', 'desc']).default('desc').describe('Sort order'),
    include_stats: z.boolean().default(true).describe('Include session statistics (prompt count, execution count)')
}).describe('Select and filter sessions with pagination and search options');
export const SelectSessionsOutputSchema = z.object({
    success: z.boolean(),
    data: z.object({
        sessions: z.array(SessionSchema),
        total: z.number(),
        limit: z.number(),
        offset: z.number()
    }).optional(),
    error: z.string().optional()
});
export const SearchSessionHistoryInputSchema = z.object({
    offset: z.number().nonnegative().default(0).describe('Number of results to skip for pagination'),
    limit: z.number().positive().max(100).default(100).describe('Maximum number of results to return (max 100)'),
    search: z.string().describe('Search term to look for in session history'),
    session_ids: z.array(z.string()).optional().describe('Limit search to specific session IDs'),
    date_range: z.object({
        from: z.string().describe('Start date for search range (ISO format)'),
        to: z.string().describe('End date for search range (ISO format)')
    }).optional().describe('Date range to limit search'),
    content_type: z.enum(['prompts', 'responses', 'both']).default('both').describe('Type of content to search'),
    include_context: z.boolean().default(true).describe('Include surrounding context in results'),
    sort_by: z.enum(['relevance', 'date', 'session']).default('relevance').describe('How to sort results')
}).describe('Search through prompts and responses in session history');
export const SearchSessionHistoryOutputSchema = z.object({
    success: z.boolean(),
    data: z.object({
        results: z.array(z.object({
            session_id: z.string(),
            session_title: z.string(),
            content: z.string(),
            content_type: z.enum(['prompt', 'response']),
            timestamp: z.date(),
            relevance_score: z.number(),
            context: z.string().optional()
        })),
        total: z.number(),
        limit: z.number(),
        offset: z.number()
    }).optional(),
    error: z.string().optional()
});
//# sourceMappingURL=mcp-tool-schemas.js.map